// src/tattoo/sharedCommentsApi.js
// Lightweight “shared comments” shim using localStorage.
// This makes /view links remember comments locally and fixes the missing export.

// Are we on a shared /view URL?
export function isSharedView() {
  if (typeof window === "undefined") return false;
  try {
    const { pathname, hash } = window.location;
    if (pathname.startsWith("/view")) return true;

    // Also treat “#s=…” links as shared, e.g. /view#s=abc123
    if (hash && hash.includes("s=")) return true;
  } catch {}
  return false;
}

// Derive a localStorage key from the share hash
function getShareStorageKey() {
  if (typeof window === "undefined") return null;
  try {
    const hash = window.location.hash || "";
    const qs = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const s = qs.get("s"); // the share token from ?s=...
    if (!s) return null;
    return `tt_shared_comments:${s}`;
  } catch {
    return null;
  }
}

/**
 * Read any stored shared comments for the current shared project.
 * Returns `{ threads: { [layerId]: Comment[] } } | null`
 */
export async function fetchSharedCommentsForCurrentProject() {
  if (typeof window === "undefined") return null;
  if (!isSharedView()) return null;

  const key = getShareStorageKey();
  if (!key) return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (err) {
    console.warn("[sharedCommentsApi] fetch failed:", err);
    return null;
  }
}

/**
 * Append a comment to the shared thread for this project.
 * `comment` is the full comment object from commentsState.addComment(...)
 */
export async function postSharedComment(layerId, comment) {
  if (typeof window === "undefined") return;
  if (!isSharedView()) return;
  if (!layerId || !comment) return;

  const key = getShareStorageKey();
  if (!key) return;

  try {
    const raw = window.localStorage.getItem(key);
    const data =
      raw && typeof raw === "string"
        ? JSON.parse(raw)
        : { threads: {} };

    if (!data.threads) data.threads = {};
    if (!Array.isArray(data.threads[layerId])) {
      data.threads[layerId] = [];
    }

    data.threads[layerId].push(comment);
    window.localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.warn("[sharedCommentsApi] post failed:", err);
  }
}